import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import {
  fetchCustom,
  fetchCommunities,
  fetchPortfolios,
  downloadCustomCsv,
  downloadCustomPdf,
  mapBackendClientId,
  type DashboardCustom,
  type DashboardCommunity,
  type CustomCommunity,
} from '../services/dashboardApi';
import { LeadSourcesPanel } from './LeadSourcesPanel';

export interface CustomReportTheme {
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  accent: string;
  highlight?: string;
  sectionTitle: string;
  sectionDivider: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  rowDivider: string;
  positive: string;
  negative: string;
}

export interface CustomReportProps {
  clientId: string;
  theme: CustomReportTheme;
  portfolioKey?: string;
}

// ─── Formatters ──────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  return n != null ? n.toLocaleString() : '—';
}

function pct(n: number | null | undefined, signed = false): string {
  if (n == null) return '—';
  const v = (n * 100).toFixed(1);
  return signed && n > 0 ? `+${v}%` : `${v}%`;
}

function pp(n: number | null | undefined): string {
  if (n == null) return '—';
  const v = (n * 100).toFixed(1);
  return `${n > 0 ? '+' : ''}${v}pp`;
}

function fmtDateRange(start: string, end: string): string {
  try {
    const s = new Date(start);
    const e = new Date(end);
    e.setDate(e.getDate() - 1); // end is exclusive; show inclusive
    const fmtOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${s.toLocaleDateString('en-US', fmtOpts)} – ${e.toLocaleDateString('en-US', fmtOpts)}`;
  } catch {
    return `${start} – ${end}`;
  }
}

function communityLabel(c: CustomCommunity): string {
  return c.community_name || c.community_unique;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const MetricCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  subTone?: 'neutral' | 'positive' | 'negative';
  /** Data source platform shown in the top-right info tooltip. Defaults to
   *  'Aline' (every MorningStar metric today); pass explicitly as more
   *  sources are added so each box declares where its data comes from. */
  source?: string;
  theme: CustomReportTheme;
}> = ({ label, value, sub, subTone = 'neutral', source = 'Aline', theme }) => {
  const subColor =
    subTone === 'positive' ? theme.positive :
    subTone === 'negative' ? theme.negative :
    theme.textMuted;
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      boxShadow: theme.cardShadow,
      borderRadius: 4,
      padding: '1.25rem 1.5rem',
      position: 'relative',
    }}>
      {source && (
        <span
          title={`Source: ${source}`}
          aria-label={`Source: ${source}`}
          style={{
            position: 'absolute', top: '0.6rem', right: '0.6rem',
            display: 'inline-flex', color: theme.textMuted, cursor: 'help',
          }}
        >
          <Info size={14} />
        </span>
      )}
      <p style={{
        fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
        textTransform: 'uppercase', color: theme.textSecondary, marginBottom: '0.5rem',
        paddingRight: '1.25rem',
      }}>{label}</p>
      <p style={{
        fontSize: '1.8rem', fontWeight: 700, color: theme.textPrimary, lineHeight: 1,
      }}>{value}</p>
      {sub && (
        <p style={{ fontSize: '0.78rem', color: subColor, marginTop: '0.4rem', fontWeight: 500 }}>
          {sub}
        </p>
      )}
    </div>
  );
};

const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  theme: CustomReportTheme;
}> = ({ title, children, theme }) => (
  <div style={{
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    boxShadow: theme.cardShadow,
    borderRadius: 4,
    padding: '1.25rem 1.5rem',
  }}>
    <h3 style={{
      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
      textTransform: 'uppercase', color: theme.sectionTitle,
      marginBottom: '1rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${theme.sectionDivider}`,
    }}>{title}</h3>
    {children}
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

export const CustomReportView: React.FC<CustomReportProps> = ({
  clientId, theme, portfolioKey = '',
}) => {
  const backendClientId = useMemo(() => mapBackendClientId(clientId), [clientId]);

  // Default to a "last 30 days, completed" window: end = today, start = 30d ago.
  // (Use a draft state so the user can edit dates without firing a fetch on
  // every keystroke. They commit via the Apply button.)
  const [draftStart, setDraftStart] = useState<string>(daysAgoISO(30));
  const [draftEnd, setDraftEnd] = useState<string>(todayISO());
  const [appliedStart, setAppliedStart] = useState<string>(draftStart);
  const [appliedEnd, setAppliedEnd] = useState<string>(draftEnd);

  const [data, setData] = useState<DashboardCustom | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingKind, setDownloadingKind] = useState<'pdf' | 'csv' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [communityFilter, setCommunityFilter] = useState<string>('ALL');
  const [communities, setCommunities] = useState<DashboardCommunity[]>([]);
  const [portfolioMembers, setPortfolioMembers] = useState<string[] | null>(null);

  const scopedCommunities = useMemo(() => {
    if (!portfolioMembers) return communities;
    const allowed = new Set(portfolioMembers);
    return communities.filter(c => allowed.has(c.community_unique));
  }, [communities, portfolioMembers]);

  const requestOpts = useMemo(() => ({
    startDate: appliedStart,
    endDate: appliedEnd,
    portfolio: portfolioKey || undefined,
    communityUnique: communityFilter !== 'ALL' ? communityFilter : undefined,
  }), [appliedStart, appliedEnd, portfolioKey, communityFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCustom(backendClientId, requestOpts);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [backendClientId, requestOpts]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchCommunities(backendClientId)
      .then(rows => { if (!cancelled) setCommunities(rows); })
      .catch(() => { /* dropdown stays empty */ });
    fetchPortfolios(backendClientId)
      .then(rows => {
        if (cancelled) return;
        if (!portfolioKey) {
          setPortfolioMembers(null);
          return;
        }
        const def = rows.find(p => p.key === portfolioKey);
        setPortfolioMembers(def ? def.community_uniques : null);
      })
      .catch(() => { /* no scoping if portfolios fail */ });
    return () => { cancelled = true; };
  }, [backendClientId, portfolioKey]);

  useEffect(() => { setCommunityFilter('ALL'); }, [portfolioKey]);

  const handleApply = () => {
    if (!draftStart || !draftEnd) return;
    if (new Date(draftEnd) <= new Date(draftStart)) {
      setError('End date must be after start date.');
      return;
    }
    setAppliedStart(draftStart);
    setAppliedEnd(draftEnd);
  };

  const handleDownload = async (kind: 'pdf' | 'csv') => {
    setDownloadingKind(kind);
    setDownloadError(null);
    try {
      if (kind === 'pdf') {
        await downloadCustomPdf(backendClientId, requestOpts);
      } else {
        await downloadCustomCsv(backendClientId, requestOpts);
      }
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingKind(null);
    }
  };

  const inputStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    padding: '0.35rem 0.55rem',
    borderRadius: 3,
    border: `1px solid ${theme.cardBorder}`,
    background: theme.cardBg,
    color: theme.textPrimary,
    fontFamily: 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.7rem',
    color: theme.textSecondary,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Header — date pickers + Apply + CSV + community filter */}
      <div style={{
        background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
        boxShadow: theme.cardShadow, borderRadius: 4, padding: '1rem 1.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: '1rem', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <p style={{
            fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase',
            color: theme.textSecondary, fontWeight: 600, margin: 0,
          }}>Custom Range Operator Report</p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.6rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label htmlFor="custom-start" style={labelStyle}>From</label>
              <input
                id="custom-start"
                type="date"
                value={draftStart}
                onChange={e => setDraftStart(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label htmlFor="custom-end" style={labelStyle}>To</label>
              <input
                id="custom-end"
                type="date"
                value={draftEnd}
                onChange={e => setDraftEnd(e.target.value)}
                style={inputStyle}
              />
            </div>
            <button
              type="button"
              onClick={handleApply}
              disabled={loading}
              style={{
                fontSize: '0.78rem', fontWeight: 600, padding: '0.45rem 1rem',
                borderRadius: 4, cursor: loading ? 'default' : 'pointer',
                fontFamily: 'inherit',
                background: theme.accent, color: '#ffffff',
                border: `1px solid ${theme.accent}`,
                opacity: loading ? 0.6 : 1,
              }}
            >
              Apply
            </button>
          </div>
          {data && data.snapshot_at && (
            <p style={{ fontSize: '0.78rem', color: theme.textPrimary, margin: 0 }}>
              <strong title="Current window">{fmtDateRange(data.this_period.start, data.this_period.end)}</strong>
              <span style={{ color: theme.textMuted }}> vs </span>
              <strong title="Prior window (defaults to immediately preceding period of equal length)">
                {fmtDateRange(data.prior_period.start, data.prior_period.end)}
              </strong>
            </p>
          )}
          {data?.snapshot_at && (
            <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: 0 }}>
              Snapshot: {new Date(data.snapshot_at).toLocaleString()}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
            <label htmlFor="custom-community-filter" style={labelStyle}>Community</label>
            <select
              id="custom-community-filter"
              value={communityFilter}
              onChange={e => setCommunityFilter(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', minWidth: 220 }}
            >
              <option value="ALL">All Communities ({scopedCommunities.length})</option>
              {scopedCommunities.map(c => (
                <option key={c.community_unique} value={c.community_unique}>
                  {c.name || c.community_unique}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => handleDownload('pdf')}
              disabled={downloadingKind !== null || !data?.snapshot_at}
              style={{
                fontSize: '0.78rem', fontWeight: 600,
                padding: '0.45rem 1rem', borderRadius: 4,
                cursor: downloadingKind !== null || !data?.snapshot_at ? 'default' : 'pointer',
                fontFamily: 'inherit',
                background: theme.accent, color: '#ffffff',
                border: `1px solid ${theme.accent}`,
                opacity: downloadingKind !== null || !data?.snapshot_at ? 0.6 : 1, whiteSpace: 'nowrap',
              }}
            >
              {downloadingKind === 'pdf' ? 'Generating…' : '⬇ PDF'}
            </button>
            <button
              onClick={() => handleDownload('csv')}
              disabled={downloadingKind !== null || !data?.snapshot_at}
              style={{
                fontSize: '0.78rem', fontWeight: 600,
                padding: '0.45rem 1rem', borderRadius: 4,
                cursor: downloadingKind !== null || !data?.snapshot_at ? 'default' : 'pointer',
                fontFamily: 'inherit',
                background: 'transparent', color: theme.accent,
                border: `1px solid ${theme.accent}`,
                opacity: downloadingKind !== null || !data?.snapshot_at ? 0.6 : 1, whiteSpace: 'nowrap',
              }}
            >
              {downloadingKind === 'csv' ? 'Generating…' : '⬇ CSV'}
            </button>
          </div>
          {downloadError && (
            <p style={{ fontSize: '0.7rem', color: theme.negative, maxWidth: 280, textAlign: 'right' }}>
              {downloadError}
            </p>
          )}
        </div>
      </div>

      {loading && !data && (
        <p style={{ color: theme.textMuted, padding: '2rem' }}>Loading custom report…</p>
      )}
      {error && (
        <p style={{ color: theme.negative, padding: '1rem 1.5rem' }}>Error: {error}</p>
      )}
      {!loading && !error && data && !data.snapshot_at && (
        <p style={{ color: theme.textMuted, padding: '2rem' }}>
          No snapshot data available yet.
        </p>
      )}

      {data && data.snapshot_at && (
        <>
          {/* Current-state cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <MetricCard
              label="Occupancy Rate"
              value={pct(data.current_state.occupancy_rate)}
              sub={`${fmt(data.current_state.occupied_units)} / ${fmt(data.current_state.total_units)} units`}
              theme={theme}
            />
            <MetricCard
              label="Active Leads"
              value={fmt(data.current_state.active_leads)}
              sub="status = Active"
              theme={theme}
            />
            <MetricCard
              label="Move-ins in Window"
              value={fmt(data.current_state.move_ins_in_window)}
              sub="gross"
              theme={theme}
            />
            <MetricCard
              label="Move-outs in Window"
              value={fmt(data.current_state.move_outs_in_window)}
              sub="gross"
              theme={theme}
            />
            <MetricCard
              label="Net Change in Window"
              value={
                data.current_state.net_move_change > 0
                  ? `+${data.current_state.net_move_change}`
                  : fmt(data.current_state.net_move_change)
              }
              sub="move-ins − move-outs"
              subTone={
                data.current_state.net_move_change > 0
                  ? 'positive'
                  : data.current_state.net_move_change < 0
                    ? 'negative'
                    : 'neutral'
              }
              theme={theme}
            />
          </div>

          {/* Period-comparison cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <MetricCard
              label="Leads This Period"
              value={fmt(data.totals.leads_cur)}
              sub={data.totals.leads_change_pct != null ? `${pct(data.totals.leads_change_pct, true)} vs prior` : 'no prior data'}
              subTone={data.totals.leads_change_pct == null ? 'neutral' : data.totals.leads_change_pct >= 0 ? 'positive' : 'negative'}
              theme={theme}
            />
            <MetricCard label="Leads Prior Period" value={fmt(data.totals.leads_pri)} sub="reference" theme={theme} />
            <MetricCard
              label="Tours This Period"
              value={fmt(data.totals.tours_cur)}
              sub={data.totals.tours_change_pct != null ? `${pct(data.totals.tours_change_pct, true)} vs prior` : 'no prior data'}
              subTone={data.totals.tours_change_pct == null ? 'neutral' : data.totals.tours_change_pct >= 0 ? 'positive' : 'negative'}
              theme={theme}
            />
            <MetricCard label="Tours Prior Period" value={fmt(data.totals.tours_pri)} sub="reference" theme={theme} />
          </div>

          {/* Lead sources panel */}
          <LeadSourcesPanel
            clientId={clientId}
            theme={theme}
            period="month"
            portfolioKey={portfolioKey}
            communityUnique={communityFilter !== 'ALL' ? communityFilter : undefined}
            referenceDate={appliedStart}
          />

          {/* Community breakdown table */}
          <SectionCard
            title={communityFilter === 'ALL' ? 'Community Breakdown' : 'Community Detail'}
            theme={theme}
          >
            <p style={{ fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.75rem' }}>
              <strong>Cur</strong> = This Period · <strong>Pri</strong> = Prior Period ·
              {' '}<strong>Δ</strong> = % change · {' '}
              <strong>FU Rate</strong> = Financially Unqualified % of leads ·
              {' '}<strong>Δ pp</strong> = delta in percentage points. Higher FU rate = more
              unqualified leads (unfavorable).
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'transparent', borderBottom: `2px solid ${theme.sectionDivider}` }}>
                    <th style={th(theme, 'left')}>Community</th>
                    <th style={th(theme)}>Leads Cur</th>
                    <th style={th(theme)}>Leads Pri</th>
                    <th style={th(theme)}>Leads Δ</th>
                    <th style={th(theme)}>Tours Cur</th>
                    <th style={th(theme)}>Tours Pri</th>
                    <th style={th(theme)}>Tours Δ</th>
                    <th style={th(theme)}>FU Rate Cur</th>
                    <th style={th(theme)}>FU Rate Pri</th>
                    <th style={th(theme)}>FU Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.communities.map(c => (
                    <tr key={c.community_unique} style={{ borderBottom: `1px solid ${theme.rowDivider}` }}>
                      <td style={td(theme, 'left')}>{communityLabel(c)}</td>
                      <td style={td(theme)}>{c.leads_cur}</td>
                      <td style={td(theme)}>{c.leads_pri}</td>
                      <td style={tdSigned(theme, c.leads_change_pct, 'leads')}>{pct(c.leads_change_pct, true)}</td>
                      <td style={td(theme)}>{c.tours_cur}</td>
                      <td style={td(theme)}>{c.tours_pri}</td>
                      <td style={tdSigned(theme, c.tours_change_pct, 'tours')}>{pct(c.tours_change_pct, true)}</td>
                      <td style={td(theme)}>{pct(c.fu_rate_cur)}</td>
                      <td style={td(theme)}>{pct(c.fu_rate_pri)}</td>
                      <td style={tdSigned(theme, c.fu_rate_delta_pp, 'fu')}>{pp(c.fu_rate_delta_pp)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: theme.cardBg, borderTop: `2px solid ${theme.sectionDivider}`, fontWeight: 600 }}>
                    <td style={td(theme, 'left')}>Total / Avg</td>
                    <td style={td(theme)}>{data.totals.leads_cur}</td>
                    <td style={td(theme)}>{data.totals.leads_pri}</td>
                    <td style={tdSigned(theme, data.totals.leads_change_pct, 'leads')}>{pct(data.totals.leads_change_pct, true)}</td>
                    <td style={td(theme)}>{data.totals.tours_cur}</td>
                    <td style={td(theme)}>{data.totals.tours_pri}</td>
                    <td style={tdSigned(theme, data.totals.tours_change_pct, 'tours')}>{pct(data.totals.tours_change_pct, true)}</td>
                    <td style={td(theme)}>{pct(data.totals.fu_rate_cur)}</td>
                    <td style={td(theme)}>{pct(data.totals.fu_rate_pri)}</td>
                    <td style={tdSigned(theme, data.totals.fu_rate_delta_pp, 'fu')}>{pp(data.totals.fu_rate_delta_pp)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
};

// ─── Table style helpers ─────────────────────────────────────────────────────

function th(theme: CustomReportTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.5rem 0.5rem',
    textAlign: align,
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.textSecondary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

function td(theme: CustomReportTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.45rem 0.5rem',
    textAlign: align,
    color: theme.textPrimary,
    whiteSpace: 'nowrap',
  };
}

function tdSigned(
  theme: CustomReportTheme,
  v: number | null | undefined,
  kind: 'leads' | 'tours' | 'fu',
): React.CSSProperties {
  let color: string = theme.textPrimary;
  if (v != null) {
    if (kind === 'fu') {
      color = v <= 0 ? theme.positive : theme.negative;
    } else {
      color = v >= 0 ? theme.positive : theme.negative;
    }
  }
  return { ...td(theme), color, fontWeight: 500 };
}

export default CustomReportView;
